'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgWarningFill from '@common/icon/SvgWarningFill';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { CustomLink } from '@/app/_components/CustomLink';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { calculateRequiredDonationAmount } from '@/app/donation/_utils/giftOrder';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { Rating } from '@/components/molecule/Rating';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { useGiftOption } from '../../../_context/GiftOptionContext';
import { DELIVERY_PERIOD_LABEL } from '../../../constants';
import { PriceInfo } from '../../GiftDetailHeader/_components/PriceInfo';
import { PrefillForm } from '../PrefillForm';
import { DesktopMainImages } from './DesktopMainImages';
import { MobileHero } from './MobileHero';
interface HeaderInfoSectionProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  productData?: GiftProductDetail;
  className?: string;
}
export const HeaderInfoSection = t0 => {
  const $ = _c(79);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    productData,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const {
    selectedOptions
  } = useGiftOption();
  let t1;
  if ($[0] !== selectedOptions) {
    t1 = Array.from(selectedOptions.values()).reduce(_temp, 0);
    $[0] = selectedOptions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalExtraProductPrice = t1;
  let t2;
  if ($[2] !== productData?.currentPrice || $[3] !== requiredDonationAmount || $[4] !== selectedOptions.size || $[5] !== totalExtraProductPrice) {
    t2 = selectedOptions.size > 0 ? calculateRequiredDonationAmount((productData?.currentPrice ?? 0) + totalExtraProductPrice) : requiredDonationAmount;
    $[2] = productData?.currentPrice;
    $[3] = requiredDonationAmount;
    $[4] = selectedOptions.size;
    $[5] = totalExtraProductPrice;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const selectedDonationAmount = t2;
  const isStoppedSale = productSaleStatus === GIFT_SALE_STATUS.STOPPED;
  const institutionRouteId = productData?.institutionSlugId ?? productData?.institutionId;
  let t3;
  if ($[7] !== onlineProductYN || $[8] !== productData?.deliveryExclusionRegionList) {
    t3 = onlineProductYN ? "" : (productData?.deliveryExclusionRegionList ?? []).map(_temp2).join(", ");
    $[7] = onlineProductYN;
    $[8] = productData?.deliveryExclusionRegionList;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const deliveryExclusionRegionNames = t3;
  const t4 = reviewRating ?? 0;
  const t5 = isDesktopViewport ? 24 : 20;
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <Rating value={t4} size={t5} />;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== reviewCount) {
    t7 = reviewCount > 0 ? <span className={cn("font-600 text-Gray-600 underline typo-body3", "mobile-tablet:typo-body4")}>{reviewCount.toLocaleString()}건 후기 보기</span> : null;
    $[13] = reviewCount;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const ratingSummary = t8;
  let t9;
  if ($[18] !== className) {
    t9 = cn("grid grid-cols-2 gap-28", "tablet:grid-cols-1 tablet:gap-0", "mobile:grid-cols-1 mobile:gap-0", className);
    $[18] = className;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== adultYN || $[21] !== heroImageUrlList) {
    t10 = <MobileHero heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[20] = adultYN;
    $[21] = heroImageUrlList;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== adultYN || $[24] !== heroImageUrlList || $[25] !== title) {
    t11 = <DesktopMainImages title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[23] = adultYN;
    $[24] = heroImageUrlList;
    $[25] = title;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20", "mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:pt-24");
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("flex flex-col px-20", "mobile-tablet:px-24");
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== institutionName || $[30] !== institutionRouteId) {
    t14 = institutionRouteId ? <CustomLink href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: institutionRouteId
    })} aria-label={`${institutionName} 상세 페이지로 이동`} className={cn("flex w-fit items-center gap-2 rounded-xsmall font-600 text-Gray-600 typo-body3", "mobile-tablet:font-400 mobile-tablet:typo-body4", "hover:underline active:opacity-80", FOCUS_RING)}>{institutionName}<SvgChevronright className="size-16 shrink-0 mobile-tablet:hidden" /></CustomLink> : <p className={cn("font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4")}>{institutionName}</p>;
    $[29] = institutionName;
    $[30] = institutionRouteId;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ShareButton />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14) {
    t16 = <div className="mb-8 flex items-center justify-between gap-4">{t14}{t15}</div>;
    $[33] = t14;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn("mb-12 flex justify-between gap-16", "mobile-tablet:mb-16");
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = isStoppedSale ? "text-Gray-400" : "text-Gray-900 mobile-tablet:text-Gray-black";
  let t19;
  if ($[36] !== t18) {
    t19 = cn("flex-1 break-all font-700 typo-h3", "tablet:font-600 tablet:typo-body1", "mobile:font-600 mobile:typo-body1", t18);
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== productName || $[39] !== t19) {
    t20 = <div className={t17}><h1 className={t19}>{productName}</h1></div>;
    $[38] = productName;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = cn("mb-28 flex flex-wrap items-center gap-8", "mobile-tablet:mb-16 mobile-tablet:gap-4");
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== ratingSummary || $[43] !== reviewCount || $[44] !== scrollToSectionWithTab) {
    t22 = <div className={t21}>{reviewCount > 0 ? <button type="button" onClick={() => scrollToSectionWithTab("review")} className={cn("flex items-center gap-8 rounded-xsmall transition-opacity duration-200", "mobile-tablet:gap-4", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500")}>{ratingSummary}</button> : <div className={cn("flex items-center gap-8", "mobile-tablet:gap-4")}>{ratingSummary}</div>}</div>;
    $[42] = ratingSummary;
    $[43] = reviewCount;
    $[44] = scrollToSectionWithTab;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== selectedDonationAmount) {
    t23 = <div className="mb-20"><PriceInfo amount={selectedDonationAmount} /></div>;
    $[46] = selectedDonationAmount;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t16 || $[49] !== t20 || $[50] !== t22 || $[51] !== t23) {
    t24 = <div className={t13}>{t16}{t20}{t22}{t23}</div>;
    $[48] = t16;
    $[49] = t20;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = cn("mx-20 flex flex-col gap-24 border-t border-Gray-200 pb-16 pt-20", "mobile-tablet:mx-0 mobile-tablet:gap-16 mobile-tablet:p-20");
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== onlineProductDesc || $[55] !== onlineProductYN) {
    t26 = onlineProductYN ? <div className={cn("flex flex-col gap-16 font-500 typo-body3", "mobile-tablet:gap-12 mobile-tablet:font-400 mobile-tablet:typo-body4")}><div className="flex items-start gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">배송 정보</p><p className="text-Gray-550 mobile-tablet:text-Gray-900">온라인 답례품</p></div>{onlineProductDesc ? <div className="flex items-start gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">답례품 설명</p><p className="whitespace-pre-line text-Gray-550 mobile-tablet:text-Gray-900">{onlineProductDesc}</p></div> : null}</div> : <div className={cn("flex flex-col gap-16 font-500 typo-body3", "mobile-tablet:gap-12 mobile-tablet:font-400 mobile-tablet:typo-body4")}><div className="flex items-center gap-56 mobile-tablet:gap-16"><p className="w-92 text-Gray-600">배송 정보</p><p className="text-Gray-550 mobile-tablet:text-Gray-900">무료배송</p></div><div className="flex gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">배송 안내</p><div className={cn("flex flex-wrap gap-x-4 text-Gray-550 mobile-tablet:text-Gray-900", "mobile-tablet:flex-col mobile-tablet:gap-4")}><p>{DELIVERY_PERIOD_LABEL}</p><p className="mobile-tablet:font-500">(식품의 경우 지연 발생 시 1주~2주 소요)</p></div></div></div>;
    $[54] = onlineProductDesc;
    $[55] = onlineProductYN;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== deliveryExclusionRegionNames || $[58] !== exchangeYN) {
    t27 = !exchangeYN || deliveryExclusionRegionNames ? <ul className="flex flex-col gap-8 rounded-xsmall border border-Gray-300 px-20 py-12 font-400 text-Gray-600 typo-caption1">{!exchangeYN ? <li className="flex items-center gap-4"><SvgWarningFill className="size-13 shrink-0 text-B-500" /><p>해당 답례품은 반품/교환이 불가해요.</p></li> : null}{deliveryExclusionRegionNames ? <li className="flex items-start gap-4"><SvgWarningFill className="mt-3 size-13 shrink-0 text-B-500" /><p className="shrink-0">배송불가 지역</p><span aria-hidden={true} className="text-Gray-500 typo-caption2">|</span><p className="text-Gray-900">{deliveryExclusionRegionNames}</p></li> : null}</ul> : null;
    $[57] = deliveryExclusionRegionNames;
    $[58] = exchangeYN;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t26 || $[61] !== t27) {
    t28 = <div className={t25}>{t26}{t27}</div>;
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== productData || $[64] !== productNumber) {
    t29 = <PrefillForm className="px-20" productData={productData} productNumber={productNumber} />;
    $[63] = productData;
    $[64] = productNumber;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Skeleton className="h-56 w-full rounded-medium" />;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== productNumber) {
    t31 = <div className="hidden px-20 pt-24 mobile-tablet:block"><Suspense fallback={t30}><EventRewardCard productNumber={productNumber} defaultExpanded={true} /></Suspense></div>;
    $[67] = productNumber;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t24 || $[70] !== t28 || $[71] !== t29 || $[72] !== t31) {
    t32 = <div className={t12}><section className="flex w-full flex-col">{t24}{t28}{t29}{t31}</section></div>;
    $[69] = t24;
    $[70] = t28;
    $[71] = t29;
    $[72] = t31;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t10 || $[75] !== t11 || $[76] !== t32 || $[77] !== t9) {
    t33 = <div className={t9}>{t10}{t11}{t32}</div>;
    $[74] = t10;
    $[75] = t11;
    $[76] = t32;
    $[77] = t9;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  return t33;
};
function _temp(total, option) {
  return total + option.extraProductPrice;
}
function _temp2(region) {
  return region.regionName;
}